import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Card,
    CardContent,
    Chip,
    Container,
    Paper,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Snackbar,
    Alert
} from "@mui/material";
import {
    collection,
    query,
    where,
    getDocs,
    getDoc,
    updateDoc,
    doc,
    serverTimestamp,
} from "firebase/firestore";
import { ExitToApp as ExitIcon } from "@mui/icons-material";
import { db } from "../../config/firebase";
import moment from "moment";
import { COLLECTION, SESSION } from "../../data/constant";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routing/routes";

const GuardDashboard = () => {
    const [otpSearch, setOtpSearch] = useState("");
    const [sessionData, setSessionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeSessions, setActiveSessions] = useState([]);
    const [guardId, setGuardId] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);

    // Snackbar State
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info", // "success", "error", "warning", "info"
    });

    const navigate = useNavigate();

    const showMessage = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar({ ...snackbar, open: false });
    };

    // 1. Authenticate & Fetch Guard Context
    useEffect(() => {
        const fetchGuardSession = async () => {
            try {
                const sessionId = localStorage.getItem(SESSION.GUARD_SESSION_ID);
                if (!sessionId) {
                    navigate(routes.GUARD_LOGIN);
                    return;
                }

                const sessionRef = doc(db, COLLECTION.GUARD_SESSION, sessionId);
                const sessionSnap = await getDoc(sessionRef);

                if (!sessionSnap.exists()) {
                    navigate(routes.GUARD_LOGIN);
                    return;
                }

                const data = sessionSnap.data();
                setGuardId(data.guardId);
                console.log("Logged in Guard ID:", data.guardId);

                // Fetch active check-ins for this guard
                fetchActiveSessions(data.guardId);

            } catch (error) {
                console.error("Auth Error:", error);
                navigate(routes.GUARD_LOGIN);
            } finally {
                setLoadingAuth(false);
            }
        };

        fetchGuardSession();
    }, [navigate]);

    const fetchActiveSessions = async (gid) => {
        try {
            const q = query(
                collection(db, COLLECTION.VISITOR_SESSIONS),
                where("guardId", "==", gid),
                where("status", "==", "CHECKED_IN")
            );
            const snap = await getDocs(q);
            const active = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setActiveSessions(active);
        } catch (error) {
            console.error("Error fetching active sessions:", error);
        }
    };

    // 2. Search Logic (OTP + Guard Filter)
    const handleSearch = async () => {
        if (!otpSearch || !guardId) return;
        setLoading(true);
        setSessionData(null);

        try {
            // Find session by OTP
            // Note: In a real app, strict OTP uniqueness across all guards is ideal, 
            // OR finding the OTP and verifying guardId matches.
            // Here we query by OTP first.
            const q = query(
                collection(db, COLLECTION.VISITOR_SESSIONS),
                where("otp", "==", otpSearch)
            );
            const snap = await getDocs(q);

            if (!snap.empty) {
                const docData = snap.docs[0].data();

                // Security Check: Does session belong to this Guard?
                if (docData.guardId !== guardId) {
                    showMessage("This OTP is allocated to a different Guard/Community.", "warning");
                    setLoading(false);
                    return;
                }

                // Security Check: Is OTP Active?
                // Allowed states: CREATED (Ready to check-in)
                // If CHECKED_IN: Already processed.
                // If CHECKED_OUT: Expired.
                if (docData.status !== "CREATED") {
                    let msg = "Invalid OTP.";
                    if (docData.status === "CHECKED_IN") msg = "Visitor already checked in.";
                    if (docData.status === "CHECKED_OUT") msg = "OTP expired (Visitor already checked out).";
                    showMessage(msg, "error");
                    setLoading(false);
                    return;
                }

                setSessionData({ id: snap.docs[0].id, ...docData });
            } else {
                showMessage("Invalid OTP.", "error");
            }
        } catch (error) {
            console.error("Search Error:", error);
            showMessage("Error searching for OTP.", "error");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (sessionId, newStatus) => {
        if (!sessionId) return;
        setActionLoading(true);

        try {
            const updatePayload = {
                status: newStatus,
            };

            if (newStatus === "CHECKED_IN") {
                updatePayload.checkedInAt = serverTimestamp();
            } else if (newStatus === "CHECKED_OUT") {
                updatePayload.checkedOutAt = serverTimestamp();
            }

            const sessionRef = doc(db, COLLECTION.VISITOR_SESSIONS, sessionId);
            await updateDoc(sessionRef, updatePayload);

            showMessage(`Success: Visitor ${newStatus === "CHECKED_IN" ? "Checked In" : "Checked Out"}`, "success");

            // Cleanup
            setSessionData(null);
            setOtpSearch("");
            fetchActiveSessions(guardId); // Refresh active list

        } catch (error) {
            console.error("Update Error:", error);
            showMessage("Failed to update status.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    if (loadingAuth) {
        return <Typography sx={{ p: 4, textAlign: 'center' }}>Loading Guard Profile...</Typography>;
    }

    return (
        <Container maxWidth="md" sx={{ marginTop: "50px", fontFamily: "Montserrat, sans-serif" }}>
            <Paper elevation={3} sx={{ padding: "30px", borderRadius: "16px" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#8b003c" }}>
                        Guard Dashboard
                    </Typography>
                    <Chip label="Active" color="success" size="small" variant="outlined" />
                </Box>

                <Typography variant="body2" color="textSecondary" mb={3}>
                    Verify Visitor OTP for Check-in.
                </Typography>

                {/* Check-In Section */}
                <Box display="flex" gap={2} mb={4}>
                    <TextField
                        label="Enter Visitor OTP"
                        variant="outlined"
                        fullWidth
                        value={otpSearch}
                        onChange={(e) => setOtpSearch(e.target.value)}
                        placeholder="6-digit OTP"
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || !otpSearch}
                        sx={{
                            backgroundColor: "#8b003c",
                            minWidth: "120px",
                            "&:hover": { backgroundColor: "#6d002f" },
                            fontWeight: "bold",
                            fontFamily: "Montserrat, sans-serif"
                        }}
                    >
                        {loading ? "Verifying..." : "Check In"}
                    </Button>
                </Box>

                <Divider />

                {/* Active Check-In Result Card */}
                {sessionData && (
                    <Box mt={4} mb={4}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Verify Details
                        </Typography>
                        <Card variant="outlined" sx={{ borderRadius: "12px", border: "1px solid #8b003c", backgroundColor: "#fff5f8" }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">Guest(s)</Typography>
                                        <Typography variant="h6" fontWeight="bold">
                                            {sessionData.guests?.join(", ")}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">Host</Typography>
                                        <Typography variant="body1">
                                            {sessionData.userName} (Pod: {sessionData.podNumber})
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Box mt={3}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        size="large"
                                        onClick={() => updateStatus(sessionData.id, "CHECKED_IN")}
                                        disabled={actionLoading}
                                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: "bold" }}
                                    >
                                        CONFIRM CHECK-IN
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Active Guests List (For Checkout) */}
                <Box mt={4}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Active Visitors (Checked In)
                        <Chip label={activeSessions.length} size="small" color="default" />
                    </Typography>

                    {activeSessions.length === 0 ? (
                        <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: '8px' }}>
                            No active visitors currently checked in.
                        </Typography>
                    ) : (
                        <List sx={{ bgcolor: '#f9f9f9', borderRadius: '8px', overflow: 'hidden' }}>
                            {activeSessions.map((session, index) => (
                                <React.Fragment key={session.id}>
                                    {index > 0 && <Divider component="li" />}
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Typography fontWeight="bold" fontFamily="Montserrat, sans-serif">
                                                    {session.guests?.join(", ")}
                                                </Typography>
                                            }
                                            secondary={
                                                <React.Fragment>
                                                    <Typography component="span" variant="body2" color="textSecondary" fontFamily="Montserrat, sans-serif">
                                                        Visiting: {session.userName} ({session.podNumber})
                                                        <br />
                                                        In: {moment(session.checkedInAt?.toDate()).format("h:mm A")}
                                                    </Typography>
                                                </React.Fragment>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                endIcon={<ExitIcon />}
                                                onClick={() => updateStatus(session.id, "CHECKED_OUT")}
                                                disabled={actionLoading}
                                                sx={{ fontFamily: "Montserrat, sans-serif", textTransform: 'none' }}
                                            >
                                                Check Out
                                            </Button>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default GuardDashboard;

